import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/utils/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead, { 
  generateLocalBusinessSchema, 
  generateBreadcrumbSchema, 
  generateItemListSchema 
} from "@/components/SEOHead";
import { Search, MapPin, MessageCircle, Phone, ChevronRight, ChevronLeft, Star, Send, Calendar } from "lucide-react";

const ADS_PER_PAGE = 25;

// SEO-friendly service name mapping
const SERVICE_SEO_DATA = {
  'escort-service': {
    name: 'Escort Service',
    title: (city) => `Escort Service in ${city} | Best Escorts ${city} | uplino.in`,
    description: (city) => `Find verified escort services in ${city}. Browse top-rated escorts with photos, contact details. Safe & discreet ${city} escort service listings on uplino.in.`,
    keywords: (city) => [
      `escort service ${city}`,
      `escorts in ${city}`,
      `${city} escort`,
      `best escort ${city}`,
      `escort ${city.toLowerCase()}`,
      `${city.toLowerCase()} escorts`,
      `female escorts ${city}`,
      `independent escorts ${city}`,
      `escort agency ${city}`,
      `vip escorts ${city}`
    ],
    h1: (city) => `Escort Service in ${city}`,
    h2: (city) => `Find Verified Escorts in ${city}`
  },
  'call-girls': {
    name: 'Call Girls',
    title: (city) => `Call Girls in ${city} | ${city} Call Girl Service | uplino.in`,
    description: (city) => `Looking for call girls in ${city}? Browse verified call girl listings with real photos & contact numbers. Best ${city} call girl service on uplino.in.`,
    keywords: (city) => [
      `call girls ${city}`,
      `call girl in ${city}`,
      `${city} call girls`,
      `call girl service ${city}`,
      `${city.toLowerCase()} call girl`,
      `call girls near me ${city}`,
      `independent call girls ${city}`,
      `best call girls ${city}`,
      `call girl number ${city}`,
      `call girl contact ${city}`
    ],
    h1: (city) => `Call Girls in ${city}`,
    h2: (city) => `Browse Call Girl Services in ${city}`
  },
  'massage-service': {
    name: 'Massage Service',
    title: (city) => `Massage Service in ${city} | Best Massage ${city} | uplino.in`,
    description: (city) => `Discover premium massage services in ${city}. Professional massage parlors, spa services, body massage. Book ${city} massage service on uplino.in.`,
    keywords: (city) => [
      `massage service ${city}`,
      `massage in ${city}`,
      `${city} massage`,
      `body massage ${city}`,
      `spa massage ${city}`,
      `massage parlour ${city}`,
      `${city.toLowerCase()} massage service`,
      `best massage ${city}`,
      `female massage ${city}`,
      `massage near me ${city}`
    ],
    h1: (city) => `Massage Service in ${city}`,
    h2: (city) => `Top Massage Parlors in ${city}`
  }
};

// Popular cities for internal linking
const POPULAR_CITIES = [
  { name: 'Mumbai', slug: 'mumbai' },
  { name: 'Delhi', slug: 'delhi' },
  { name: 'Bangalore', slug: 'bangalore' },
  { name: 'Hyderabad', slug: 'hyderabad' },
  { name: 'Chennai', slug: 'chennai' },
  { name: 'Kolkata', slug: 'kolkata' },
  { name: 'Pune', slug: 'pune' },
  { name: 'Ahmedabad', slug: 'ahmedabad' },
  { name: 'Jaipur', slug: 'jaipur' },
  { name: 'Goa', slug: 'goa' }
];

// Premium Ad Card Component with auto slideshow
const PremiumAdCard = ({ ad, index, cityName, serviceName, navigate, isGlobalPinned = false, freeAdsAsTop = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Determine if this ad should display as a "top" ad
  // Either it's a global pinned ad, OR it's a free ad and freeAdsAsTop is enabled
  const displayAsTop = isGlobalPinned || (ad.is_free && freeAdsAsTop);
  const showBlur = ad.is_free && !freeAdsAsTop; // Only blur if free and freeAdsAsTop is NOT enabled
  const showContactButtons = !ad.is_free || freeAdsAsTop; // Show contact buttons if paid OR if freeAdsAsTop is enabled

  // Auto slideshow for global pinned ads OR free ads displayed as top
  useEffect(() => {
    if (displayAsTop && ad.images && ad.images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % ad.images.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [displayAsTop, ad.images]);

  const nextImage = (e) => {
    e.stopPropagation();
    if (ad.images && ad.images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % ad.images.length);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (ad.images && ad.images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + ad.images.length) % ad.images.length);
    }
  };

  return (
    <article 
      className="group"
      itemScope 
      itemType="https://schema.org/Service"
    >
      <Card
        className={`bg-white overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer border-0 ${
          displayAsTop ? 'ring-2 ring-yellow-400/50 shadow-lg shadow-yellow-500/20' : 'shadow-lg'
        }`}
        onClick={() => navigate(`/ad/${ad.id}`)}
        data-testid={`ad-card-${index}`}
      >
        <div className="flex">
          {/* Left: 9:16 Portrait Photo */}
          <div className={`relative flex-shrink-0 bg-gradient-to-br from-gray-100 to-gray-200 ${
            displayAsTop ? 'w-36 sm:w-44 md:w-48' : 'w-32 sm:w-40 md:w-44'
          }`}>
            <div className={`relative ${displayAsTop ? 'aspect-[9/16]' : 'aspect-[9/14]'} overflow-hidden`}>
              {ad.images && ad.images.length > 0 ? (
                <>
                  <img
                    src={ad.images[currentImageIndex]}
                    alt={`${ad.title} - ${serviceName} in ${cityName}`}
                    className={`w-full h-full object-cover transition-all duration-500 ${showBlur ? 'blur-md' : ''}`}
                    loading={index < 4 ? "eager" : "lazy"}
                    itemProp="image"
                  />
                  
                  {/* Image Navigation for multiple images */}
                  {ad.images.length > 1 && !showBlur && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      
                      {/* Image dots indicator */}
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                        {ad.images.map((_, i) => (
                          <span
                            key={i}
                            className={`w-1.5 h-1.5 rounded-full transition-all ${
                              i === currentImageIndex ? 'bg-white' : 'bg-white/50'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl bg-gradient-to-br from-pink-100 to-purple-100">
                  📸
                </div>
              )}
              
              {/* Free ad overlay - only show if blur is active */}
              {showBlur && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl mb-1">🔒</div>
                    <p className="text-white text-xs font-medium">Click to View</p>
                  </div>
                </div>
              )}
              
              {/* TOP badge - show for global pinned OR free ads displayed as top */}
              {displayAsTop && (
                <div className="absolute top-2 left-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-[10px] sm:text-xs px-2 py-1 rounded-full flex items-center gap-1 font-bold shadow-lg">
                  <Star className="w-3 h-3" /> TOP
                </div>
              )}
              
              {/* Free badge - only show when not displayed as top */}
              {ad.is_free && !freeAdsAsTop && (
                <div className="absolute top-2 right-2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                  FREE
                </div>
              )}
            </div>
          </div>

          {/* Right: Content */}
          <div className="flex-1 p-3 sm:p-4 flex flex-col min-w-0">
            {/* Title */}
            <h3 
              className={`font-bold text-pink-600 mb-2 line-clamp-2 leading-tight ${
                isGlobalPinned ? 'text-base sm:text-lg' : 'text-sm sm:text-base'
              }`}
              itemProp="name"
            >
              {ad.title}
            </h3>

            {/* Description */}
            <p 
              className={`text-gray-600 mb-3 line-clamp-3 flex-grow ${
                isGlobalPinned ? 'text-sm' : 'text-xs sm:text-sm'
              }`}
              itemProp="description"
            >
              {ad.description}
            </p>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-3">
              {ad.age && (
                <span className="flex items-center gap-0.5 bg-gray-100 px-2 py-1 rounded-full">
                  <Calendar className="w-3 h-3" /> {ad.age} Yrs
                </span>
              )}
              <span className="flex items-center gap-0.5 bg-gray-100 px-2 py-1 rounded-full">
                <MapPin className="w-3 h-3" /> 
                {ad.is_global_pinned ? "India" : (ad.city_names?.[0] || cityName)}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 mt-auto">
              {showContactButtons ? (
                /* Show contact buttons for paid ads OR free ads when freeAdsAsTop is enabled */
                <>
                  {ad.contact_phone && (
                    <Button
                      size="sm"
                      className="h-10 w-10 sm:h-11 sm:w-11 p-0 bg-pink-50 hover:bg-pink-100 text-pink-600 rounded-xl border border-pink-200 shadow-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.location.href = `tel:${ad.contact_phone}`;
                      }}
                      aria-label="Call"
                    >
                      <Phone className="w-5 h-5" />
                    </Button>
                  )}
                  {ad.contact_whatsapp && (
                    <Button
                      size="sm"
                      className="h-10 w-10 sm:h-11 sm:w-11 p-0 bg-green-500 hover:bg-green-600 text-white rounded-xl shadow-md shadow-green-500/30"
                      onClick={(e) => {
                        e.stopPropagation();
                        const message = encodeURIComponent(`Hi, I saw your ad "${ad.title}" on uplino.in and I'm interested.`);
                        window.open(`https://wa.me/${ad.contact_whatsapp}?text=${message}`, '_blank');
                      }}
                      aria-label="WhatsApp"
                    >
                      <MessageCircle className="w-5 h-5" />
                    </Button>
                  )}
                  {ad.contact_telegram && (
                    <Button
                      size="sm"
                      className="h-10 w-10 sm:h-11 sm:w-11 p-0 bg-blue-500 hover:bg-blue-600 text-white rounded-xl shadow-md shadow-blue-500/30"
                      onClick={(e) => {
                        e.stopPropagation();
                        const telegramId = ad.contact_telegram.replace(/[^0-9a-zA-Z_]/g, '');
                        const isUsername = isNaN(telegramId);
                        if (isUsername) {
                          window.open(`https://t.me/${telegramId}`, '_blank');
                        } else {
                          window.open(`https://t.me/+${telegramId}`, '_blank');
                        }
                      }}
                      aria-label="Telegram"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  )}
                </>
              ) : (
                /* Free ads with freeAdsAsTop disabled - show View Details button */
                <Button
                  size="sm"
                  className="h-9 px-4 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white text-xs font-semibold rounded-lg shadow-md"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/ad/${ad.id}`);
                  }}
                >
                  View Details
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>
    </article>
  );
};

const CityServicePage = () => {
  const { city, category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ads, setAds] = useState([]);
  const [totalAds, setTotalAds] = useState(0);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [cityData, setCityData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [freeAdsAsTop, setFreeAdsAsTop] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const navigate = useNavigate();

  // Get current page from URL params (default to 1)
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const totalPages = Math.ceil(totalAds / ADS_PER_PAGE);

  useEffect(() => {
    loadData();
    loadFreeAdsAsTopSetting();
  }, [city, category, currentPage]);

  const loadFreeAdsAsTopSetting = async () => {
    try {
      const response = await api.get("/settings/free-ads-as-top");
      setFreeAdsAsTop(response.data.free_ads_as_top || false);
    } catch (error) {
      console.error("Failed to load free ads as top setting:", error);
    }
  };

  const loadData = async () => {
    try {
      const [categoriesRes, citiesRes] = await Promise.all([
        api.get("/categories"),
        api.get("/cities"),
      ]);
      setCategories(categoriesRes.data);
      setCities(citiesRes.data);

      // Find city and category by slug
      const cityObj = citiesRes.data.find(c => c.slug === city);
      const categoryObj = categoriesRes.data.find(c => c.slug === category);

      setCityData(cityObj);
      setCategoryData(categoryObj);

      if (cityObj) setSelectedCity(cityObj.id);
      if (categoryObj) setSelectedCategory(categoryObj.id);

      // Load ads
      loadAds(cityObj?.id, categoryObj?.id);
    } catch (error) {
      toast.error("Failed to load data");
      setLoading(false);
    }
  };

  const loadAds = async (cityId, categoryId) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (cityId) params.append('city_id', cityId);
      if (categoryId && categoryId !== 'all') params.append('category_id', categoryId);
      if (searchQuery) params.append('search', searchQuery);
      
      // Add pagination params
      const skip = (currentPage - 1) * ADS_PER_PAGE;
      params.append('skip', skip.toString());
      params.append('limit', ADS_PER_PAGE.toString());

      const response = await api.get(`/ads?${params.toString()}`);
      setAds(response.data);
      
      // Get total count for pagination (separate request without skip/limit)
      const countParams = new URLSearchParams();
      if (cityId) countParams.append('city_id', cityId);
      if (categoryId && categoryId !== 'all') countParams.append('category_id', categoryId);
      if (searchQuery) countParams.append('search', searchQuery);
      countParams.append('count_only', 'true');
      
      try {
        const countResponse = await api.get(`/ads/count?${countParams.toString()}`);
        setTotalAds(countResponse.data.count || response.data.length);
      } catch {
        // If count endpoint doesn't exist, estimate from response
        setTotalAds(response.data.length >= ADS_PER_PAGE ? response.data.length + 1 : response.data.length);
      }
    } catch (error) {
      toast.error("Failed to load ads");
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setSearchParams({ page: newPage.toString() });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearch = () => {
    // Reset to page 1 when searching
    setSearchParams({ page: '1' });
    loadAds(selectedCity, selectedCategory);
  };

  // Get SEO data for current page
  const seoData = SERVICE_SEO_DATA[category] || SERVICE_SEO_DATA['escort-service'];
  const cityName = cityData?.name || city?.charAt(0).toUpperCase() + city?.slice(1) || 'India';
  const serviceName = seoData.name;
  const canonicalUrl = `https://uplino.in/${city}/${category}`;

  // Generate combined schema
  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [
      generateLocalBusinessSchema(cityName, serviceName, canonicalUrl),
      generateBreadcrumbSchema([
        { name: 'Home', url: 'https://uplino.in' },
        { name: cityName, url: `https://uplino.in/${city}` },
        { name: serviceName, url: canonicalUrl }
      ]),
      ...(ads.length > 0 ? [generateItemListSchema(ads, cityName, serviceName)] : [])
    ]
  };

  // Get other services for internal linking
  const otherServices = Object.entries(SERVICE_SEO_DATA)
    .filter(([slug]) => slug !== category)
    .map(([slug, data]) => ({ slug, name: data.name }));

  // Get nearby cities for internal linking (exclude current)
  const nearbyCities = POPULAR_CITIES.filter(c => c.slug !== city).slice(0, 6);

  // Separate global pinned ads from regular ads
  const globalPinnedAds = ads.filter(ad => ad.is_global_pinned);
  const regularAds = ads.filter(ad => !ad.is_global_pinned);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* SEO Head Component */}
      <SEOHead
        title={seoData.title(cityName)}
        description={seoData.description(cityName)}
        keywords={seoData.keywords(cityName)}
        canonical={canonicalUrl}
        ogType="website"
        schema={combinedSchema}
      />

      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-4 sm:mb-6">
          <ol className="flex items-center space-x-2 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" className="text-gray-400 hover:text-white" itemProp="item">
                <span itemProp="name">Home</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-gray-400" itemProp="name">{cityName}</span>
              <meta itemProp="position" content="2" />
            </li>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-white font-medium" itemProp="name">{serviceName}</span>
              <meta itemProp="position" content="3" />
            </li>
          </ol>
        </nav>

        {/* Main Heading */}
        <header className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2" style={{fontFamily: 'Inter, sans-serif'}}>
            {seoData.h1(cityName)}
          </h1>
          <h2 className="text-base sm:text-lg text-gray-400">
            {seoData.h2(cityName)} - {totalAds > 0 ? `${totalAds} listings` : `${ads.length} listings`} available
            {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
          </h2>
        </header>

        {/* Collapsible Search Bar */}
        <div className="mb-6 sm:mb-8">
          {/* Main Search Bar - Always Visible */}
          <div 
            className="relative cursor-pointer"
            onClick={() => setSearchExpanded(!searchExpanded)}
          >
            <div className="flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3 hover:bg-white/15 transition-all">
              <Search className="w-5 h-5 text-gray-400 mr-3" />
              <span className="text-gray-400 flex-1">Search...</span>
              <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${searchExpanded ? 'rotate-90' : ''}`} />
            </div>
          </div>

          {/* Expanded Search Options */}
          {searchExpanded && (
            <Card className="mt-3 p-4 bg-white/5 backdrop-blur-sm border-white/10 animate-in slide-in-from-top-2 duration-200">
              <div className="space-y-3">
                {/* City Select */}
                <Select value={selectedCity} onValueChange={(val) => {
                  setSelectedCity(val);
                  const newCitySlug = cities.find(c => c.id === val)?.slug;
                  if (newCitySlug) navigate(`/${newCitySlug}/${category}`);
                }}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-12" data-testid="city-select">
                    <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                    <SelectValue placeholder="Select City" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Category Select */}
                <Select value={selectedCategory} onValueChange={(val) => {
                  setSelectedCategory(val);
                  const newCatSlug = categories.find(c => c.id === val)?.slug;
                  if (newCatSlug) navigate(`/${city}/${newCatSlug}`);
                }}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-12" data-testid="category-select">
                    <SelectValue placeholder="Select Service" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Search Input */}
                <Input
                  type="text"
                  placeholder="Search listings..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                  data-testid="search-input"
                />

                {/* Search Button */}
                <Button
                  onClick={handleSearch}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white h-12"
                  data-testid="search-button"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>
            </Card>
          )}
        </div>

        {/* Ads Listings */}
        <section aria-label={`${serviceName} listings in ${cityName}`}>
          {loading ? (
            <div className="text-center py-20">
              <div className="text-white text-xl">Loading listings...</div>
            </div>
          ) : ads.length === 0 ? (
            <Card className="p-8 sm:p-12 bg-white/5 backdrop-blur-sm border-white/10 text-center">
              <p className="text-gray-400 text-base sm:text-lg mb-4">No {serviceName.toLowerCase()} listings found in {cityName}</p>
              <p className="text-gray-500 text-sm">Check back later or browse other cities below</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Global Pinned Ads - Featured Section */}
              {globalPinnedAds.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="w-5 h-5 text-yellow-400" />
                    <h3 className="text-lg font-semibold text-white">Featured Ads</h3>
                  </div>
                  <div className="space-y-4">
                    {globalPinnedAds.map((ad, index) => (
                      <PremiumAdCard
                        key={ad.id}
                        ad={ad}
                        index={index}
                        cityName={cityName}
                        serviceName={serviceName}
                        navigate={navigate}
                        isGlobalPinned={true}
                        freeAdsAsTop={freeAdsAsTop}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Regular Ads */}
              {regularAds.length > 0 && (
                <div className="space-y-3">
                  {regularAds.map((ad, index) => (
                    <PremiumAdCard
                      key={ad.id}
                      ad={ad}
                      index={globalPinnedAds.length + index}
                      cityName={cityName}
                      serviceName={serviceName}
                      navigate={navigate}
                      isGlobalPinned={false}
                      freeAdsAsTop={freeAdsAsTop}
                    />
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8 pt-6 border-t border-white/10">
                  {/* Previous Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Prev
                  </Button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {/* First page */}
                    {currentPage > 3 && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(1)}
                          className="w-9 h-9 border-white/20 text-white hover:bg-white/10"
                        >
                          1
                        </Button>
                        {currentPage > 4 && (
                          <span className="text-gray-500 px-1">...</span>
                        )}
                      </>
                    )}

                    {/* Page numbers around current */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        // Show pages within 2 of current page
                        return Math.abs(page - currentPage) <= 2;
                      })
                      .map(page => (
                        <Button
                          key={page}
                          variant={page === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                          className={`w-9 h-9 ${
                            page === currentPage 
                              ? 'bg-pink-600 hover:bg-pink-700 text-white border-0' 
                              : 'border-white/20 text-white hover:bg-white/10'
                          }`}
                          data-testid={`pagination-page-${page}`}
                        >
                          {page}
                        </Button>
                      ))
                    }

                    {/* Last page */}
                    {currentPage < totalPages - 2 && (
                      <>
                        {currentPage < totalPages - 3 && (
                          <span className="text-gray-500 px-1">...</span>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(totalPages)}
                          className="w-9 h-9 border-white/20 text-white hover:bg-white/10"
                        >
                          {totalPages}
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Next Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="pagination-next"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>

        {/* SEO Content Section */}
        <section className="mt-12 sm:mt-16 mb-8 sm:mb-12">
          <Card className="p-6 sm:p-8 bg-white/5 backdrop-blur-sm border-white/10">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              About {serviceName} in {cityName}
            </h2>
            <div className="prose prose-invert max-w-none">
              <p className="text-gray-400 mb-4 text-sm sm:text-base">
                Looking for reliable {serviceName.toLowerCase()} in {cityName}? uplino.in is your trusted platform for finding 
                verified service providers in {cityName}. Our listings are regularly updated with real photos and 
                contact information to ensure you have the best experience.
              </p>
              <p className="text-gray-400 mb-4 text-sm sm:text-base">
                Whether you are searching for {serviceName.toLowerCase()} in popular areas of {cityName} or nearby locations, 
                our comprehensive directory helps you find exactly what you are looking for.
              </p>
            </div>
          </Card>
        </section>

        {/* Internal Linking - Other Services */}
        <section className="mb-8 sm:mb-12">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-4">
            Other Services in {cityName}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {otherServices.map((service) => (
              <Link
                key={service.slug}
                to={`/${city}/${service.slug}`}
                className="p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-all"
              >
                <span className="text-white font-medium">{service.name} in {cityName}</span>
                <ChevronRight className="inline w-4 h-4 ml-2 text-gray-400" />
              </Link>
            ))}
          </div>
        </section>

        {/* Internal Linking - Other Cities */}
        <section className="mb-8 sm:mb-12">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-4">
            {serviceName} in Other Cities
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3">
            {nearbyCities.map((c) => (
              <Link
                key={c.slug}
                to={`/${c.slug}/${category}`}
                className="p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-center transition-all"
              >
                <span className="text-white text-sm">{c.name}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-8 sm:mb-12" itemScope itemType="https://schema.org/FAQPage">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h3>
          <div className="space-y-3 sm:space-y-4">
            <div className="p-4 bg-white/5 border border-white/10 rounded-lg" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h4 className="text-white font-medium mb-2 text-sm sm:text-base" itemProp="name">
                How to find {serviceName.toLowerCase()} in {cityName}?
              </h4>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-gray-400 text-xs sm:text-sm" itemProp="text">
                  Browse our verified listings above to find {serviceName.toLowerCase()} providers in {cityName}. 
                  Each listing includes photos, contact details, and availability information.
                </p>
              </div>
            </div>
            <div className="p-4 bg-white/5 border border-white/10 rounded-lg" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h4 className="text-white font-medium mb-2 text-sm sm:text-base" itemProp="name">
                Are the listings verified?
              </h4>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-gray-400 text-xs sm:text-sm" itemProp="text">
                  Yes, all listings on uplino.in are verified by our team. Featured listings receive additional verification.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CityServicePage;
